import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Meervoudige selectie in de berichtenlijst.
 *
 * `replace` = gewone klik (alleen dit bericht), `toggle` = ctrl/cmd-klik of een
 * vinkje, `range` = shift-klik vanaf het anker.
 */
export type SelectIntent = "replace" | "toggle" | "range";

export interface MessageSelection {
  selected: ReadonlySet<number>;
  /** Staan de vinkjes aan? Op mobiel blijft dat staan tot "Annuleren". */
  mode: boolean;
  has: (uid: number) => boolean;
  select: (uid: number, intent: SelectIntent) => void;
  /** Zet de keuzemodus aan en selecteert meteen dit bericht (long-press). */
  begin: (uid: number) => void;
  selectAll: () => void;
  clear: () => void;
  /** Haalt uids uit de selectie nadat ze verplaatst of verwijderd zijn. */
  drop: (uids: readonly number[]) => void;
}

/**
 * @param orderedUids De uids in de volgorde waarin ze op het scherm staan —
 *   shift-klik moet het zichtbare bereik pakken, niet het numerieke.
 * @param folderPath Wisselt de map, dan is elke uid van een andere mailbox en
 *   moet de selectie leeg. Dit is geen nettigheid: uids zijn map-gebonden, dus
 *   een selectie die een mapwissel overleeft verplaatst straks de verkeerde post.
 */
export function useMessageSelection(
  orderedUids: readonly number[],
  folderPath: string | null,
): MessageSelection {
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const [sticky, setSticky] = useState(false);
  const anchor = useRef<number | null>(null);

  useEffect(() => {
    setSelected(new Set());
    setSticky(false);
    anchor.current = null;
  }, [folderPath]);

  /**
   * Bij een nieuwe pagina snoeien, niet wissen. De lijst wordt elke minuut
   * ververst door de poller; wissen zou de selectie van de gebruiker dan
   * spontaan onder zijn handen vandaan halen.
   */
  useEffect(() => {
    setSelected((current) => {
      if (current.size === 0) return current;
      const zichtbaar = new Set(orderedUids);
      const next = new Set([...current].filter((uid) => zichtbaar.has(uid)));
      return next.size === current.size ? current : next;
    });
  }, [orderedUids]);

  const select = useCallback(
    (uid: number, intent: SelectIntent) => {
      setSelected((current) => {
        if (intent === "replace") {
          anchor.current = uid;
          return new Set([uid]);
        }

        if (intent === "toggle") {
          anchor.current = uid;
          const next = new Set(current);
          if (!next.delete(uid)) next.add(uid);
          return next;
        }

        // range: vanaf het anker tot hier, in schermvolgorde. Zonder anker
        // (eerste klik was een shift-klik) valt het terug op één bericht.
        const from = anchor.current === null ? uid : anchor.current;
        const start = orderedUids.indexOf(from);
        const end = orderedUids.indexOf(uid);
        if (start === -1 || end === -1) return new Set([uid]);
        const [lo, hi] = start <= end ? [start, end] : [end, start];
        return new Set(orderedUids.slice(lo, hi + 1));
      });
    },
    [orderedUids],
  );

  const begin = useCallback((uid: number) => {
    anchor.current = uid;
    setSticky(true);
    setSelected(new Set([uid]));
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(orderedUids));
  }, [orderedUids]);

  const clear = useCallback(() => {
    anchor.current = null;
    setSticky(false);
    setSelected(new Set());
  }, []);

  const drop = useCallback((uids: readonly number[]) => {
    setSelected((current) => {
      const next = new Set(current);
      for (const uid of uids) next.delete(uid);
      return next;
    });
  }, []);

  const has = useCallback((uid: number) => selected.has(uid), [selected]);

  return {
    selected,
    mode: sticky || selected.size > 0,
    has,
    select,
    begin,
    selectAll,
    clear,
    drop,
  };
}
